import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Chip,
  Divider,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  CalendarToday,
  AttachMoney,
  Home,
  LocationOn,
  Group,
  Payment,
  People,
  Message,
  Edit,
  Email,
  WhatsApp,
  CheckCircle,
  PendingActions,
  Star,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { SidebarLayout } from '@/components/Layout';
import { Booking, Listing } from '@/types';
import { BookingsService } from '@/utils/bookings-service';
import { ListingsService } from '@/utils/listings-service';
import { guestKeys } from '@/hooks/api/useGuests';
import { bookingKeys } from '@/hooks/api/useBookings';
import { calendarBookingsKeys } from '@/hooks/api/useCalendarBookings';
import { useAuth } from '@/contexts/AuthContext';

import { BookingCommunicationPanel } from '@/components/Bookings/BookingCommunicationPanel';
import { BookingConfirmationStatus } from '@/components/Bookings/BookingConfirmationStatus';
import { BookingForm } from '@/components/Bookings/BookingForm';
import { GuestDetailsPanel } from '@/components/Bookings/GuestDetailsPanel';
import { ReviewPromptModal } from '@/components/Reviews/ReviewPromptModal';
import { ReviewEligibilityService } from '@/utils/review-eligibility-service';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`booking-details-tabpanel-${index}`}
      aria-labelledby={`booking-details-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

export default function BookingDetail() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { userProfile } = useAuth();

  const [booking, setBooking] = useState<Booking | null>(null);
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const [editFormOpen, setEditFormOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  // Extract calendar context from location state
  const calendarContext = location.state as {
    fromCalendar?: boolean;
    selectedDate?: string;
    currentMonth?: string;
    selectedListings?: string[];
  } | null;


  const loadBookingData = useCallback(async () => {
    if (!bookingId) return;

    setLoading(true);
    setError(null);

    try {
      const bookingData = await BookingsService.getBooking(bookingId);
      setBooking(bookingData);

      // Load listing data if available
      if (bookingData.listingId) {
        try {
          const listingData = await ListingsService.getListing(bookingData.listingId);
          setListing(listingData);
        } catch (listingError) {
          console.warn('Failed to load listing data:', listingError);
          // Don't set error state for listing failure, booking data is more important
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load booking details');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);
  useEffect(() => {
    if (bookingId) {
      loadBookingData();
    }
  }, [bookingId, loadBookingData]);

  const handleBack = () => {
    if (calendarContext?.fromCalendar) {
      // Navigate back to calendar with preserved state
      navigate('/calendar', {
        state: {
          selectedDate: calendarContext.selectedDate,
          currentMonth: calendarContext.currentMonth,
          selectedListings: calendarContext.selectedListings,
          restoreContext: true,
        },
      });
    } else {
      // Default back navigation
      navigate('/bookings');
    }
  };

  const handleEdit = () => {
    setEditFormOpen(true);
  };

  const handleUpdateBooking = async (bookingData: any) => {
    if (!booking) return;

    try {
      const updatedBooking = await BookingsService.updateBooking(booking.bookingId, bookingData);
      setBooking(updatedBooking);
      setEditFormOpen(false);

      // Invalidate all booking-related queries to ensure fresh data everywhere
      queryClient.invalidateQueries({ queryKey: guestKeys.bookingsWithStatus() });
      queryClient.invalidateQueries({ queryKey: guestKeys.detail(booking.bookingId) });
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(booking.bookingId) });
      queryClient.invalidateQueries({ queryKey: calendarBookingsKeys.all });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update booking');
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!booking) return;

    try {
      const updatedBooking = await BookingsService.updateBookingStatus(booking.bookingId, status as any);
      setBooking(updatedBooking);

      // Invalidate all booking-related queries to ensure fresh data everywhere
      queryClient.invalidateQueries({ queryKey: guestKeys.bookingsWithStatus() });
      queryClient.invalidateQueries({ queryKey: guestKeys.detail(booking.bookingId) });
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(booking.bookingId) });
      queryClient.invalidateQueries({ queryKey: calendarBookingsKeys.all });
      // Also invalidate booking confirmation status
      queryClient.invalidateQueries({ queryKey: ['booking-confirmation-status', booking.bookingId] });
      queryClient.invalidateQueries({ queryKey: ['booking-communications', booking.bookingId] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update booking status');
    }
  };

  const handleCancel = async () => {
    if (!booking) return;

    if (!confirm('Are you sure you want to cancel this booking? A cancellation notification will be sent to the guest.')) {
      return;
    }

    try {
      const result = await BookingsService.cancelBooking(booking.bookingId, booking.listingId);
      setBooking(result.booking);

      // Invalidate all booking-related queries to ensure fresh data everywhere
      queryClient.invalidateQueries({ queryKey: guestKeys.bookingsWithStatus() });
      queryClient.invalidateQueries({ queryKey: guestKeys.detail(booking.bookingId) });
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(booking.bookingId) });
      queryClient.invalidateQueries({ queryKey: calendarBookingsKeys.all });
      // Also invalidate booking confirmation status
      queryClient.invalidateQueries({ queryKey: ['booking-confirmation-status', booking.bookingId] });
      queryClient.invalidateQueries({ queryKey: ['booking-communications', booking.bookingId] });

      setError(null);
      // You could show a success message here
      console.log(result.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel booking');
    }
  };

  // Check if user has host privileges - booking details are host-only
  if (!userProfile?.roles?.includes('host')) {
    return (
      <SidebarLayout>
        <Box sx={{ maxWidth: 800, mx: 'auto', py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Access Denied: This page is only available to hosts. Guests can view their trip details from the My Trips page.
          </Alert>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/my-trips')} 
            startIcon={<ArrowBack />}
          >
            Go to My Trips
          </Button>
        </Box>
      </SidebarLayout>
    );
  }

  if (loading) {
    return (
      <SidebarLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </SidebarLayout>
    );
  }

  if (error || !booking) {
    return (
      <SidebarLayout>
        <Box sx={{ maxWidth: 800, mx: 'auto', py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Booking not found'}
          </Alert>
          <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBack />}>
            Go Back
          </Button>
        </Box>
      </SidebarLayout>
    );
  }

  const duration = BookingsService.calculateDuration(booking.startDate, booking.endDate);
  const dateRange = BookingsService.formatDateRange(booking.startDate, booking.endDate);
  const statusColor = BookingsService.getStatusColor(booking.status);

  // Check if booking is eligible for review
  const isEligibleForReview = ReviewEligibilityService.isBookingCompleted(booking) || 
                              ReviewEligibilityService.hasBookingEnded(booking);

  const handleWriteReview = () => {
    setReviewModalOpen(true);
  };

  const handleReviewModalClose = () => {
    setReviewModalOpen(false);
  };

  const handleReviewSubmitted = () => {
    // Optionally refresh booking data or show success message
    console.log('Review submitted successfully');
  };

  return (
    <SidebarLayout>
      <Box sx={{ maxWidth: 1200, mx: 'auto', py: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Booking #{booking.bookingId.slice(-8)}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {calendarContext?.fromCalendar ? 'From Calendar' : 'Booking Details'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
              color={statusColor}
              size="medium"
            />
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={handleEdit}
            >
              Edit
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab
              label="Booking Info"
              icon={<Person />}
              iconPosition="start"
            />
            <Tab
              label="Guest Details"
              icon={<People />}
              iconPosition="start"
            />
            <Tab
              label="Communication"
              icon={<Message />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Booking Information */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person color="primary" />
                Booking Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Booking ID
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    #{booking.bookingId}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Guest Email
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {booking.primaryGuestContact?.email || booking.guestId}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Created Date
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Date(booking.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Date(booking.updatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Main Guest Information */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person color="primary" />
                Main Guest Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Guest Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {booking.primaryGuestContact?.name || 'Not provided'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Email Address
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {booking.primaryGuestContact?.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    WhatsApp Number
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {booking.primaryGuestContact?.whatsappNumber || 'Not provided'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Preferred Contact Method
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {booking.primaryGuestContact?.preferredMethod === 'email' && (
                      <Chip
                        icon={<Email fontSize="small" />}
                        label="Email"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {booking.primaryGuestContact?.preferredMethod === 'whatsapp' && (
                      <Chip
                        icon={<WhatsApp fontSize="small" />}
                        label="WhatsApp"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    )}
                    {booking.primaryGuestContact?.preferredMethod === 'both' && (
                      <>
                        <Chip
                          icon={<Email fontSize="small" />}
                          label="Email"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          icon={<WhatsApp fontSize="small" />}
                          label="WhatsApp"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </>
                    )}
                    {!booking.primaryGuestContact?.preferredMethod && (
                      <Chip
                        icon={<Email fontSize="small" />}
                        label="Email (Default)"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Expected Guests
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {booking.expectedGuests || booking.guests}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Check-in Status
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      icon={<CheckCircle fontSize="small" />}
                      label={`${booking.guestCheckInStatus?.completed || 0} completed`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      icon={<PendingActions fontSize="small" />}
                      label={`${Math.max(0, (booking.expectedGuests || booking.guests) - (booking.guestCheckInStatus?.completed || 0))} pending`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Property Information */}
            {listing && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Home color="primary" />
                  Property Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Property Name
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {listing.title}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn fontSize="small" />
                      Address
                    </Typography>
                    <Typography variant="body1">
                      {listing.address?.street}, {listing.address?.city}, {listing.address?.state} {listing.address?.zipCode}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Bedrooms
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {listing.bedrooms}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Bathrooms
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {listing.bathrooms}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Max Guests
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {listing.maxGuests}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Stay Information */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday color="primary" />
                Stay Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Check-in Date
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Date(booking.startDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Check-out Date
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Date(booking.endDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {duration} night{duration !== 1 ? 's' : ''}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Group fontSize="small" />
                    Number of Guests
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {booking.guests}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Payment Information */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney color="primary" />
                Payment Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Amount
                  </Typography>
                  <Typography variant="h5" color="primary" fontWeight="bold">
                    ${booking.totalPrice.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Payment fontSize="small" />
                    Payment Status
                  </Typography>
                  <Chip
                    label={booking.paymentStatus.charAt(0).toUpperCase() + booking.paymentStatus.slice(1)}
                    color={booking.paymentStatus === 'paid' ? 'success' : booking.paymentStatus === 'refunded' ? 'error' : 'warning'}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                {listing && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Price Breakdown
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        Base Rate: ${listing.pricing.basePrice} × {duration} nights
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        ${(listing.pricing.basePrice * duration).toFixed(2)}
                      </Typography>
                    </Grid>
                    {listing.pricing.cleaningFee && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          Cleaning Fee
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          ${listing.pricing.cleaningFee.toFixed(2)}
                        </Typography>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Paper>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {booking.status === 'pending' && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleStatusChange('confirmed')}
                >
                  Confirm Booking
                </Button>
              )}
              {booking.status === 'confirmed' && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleStatusChange('completed')}
                >
                  Mark as Completed
                </Button>
              )}
              {isEligibleForReview && (
                <Button
                  variant="outlined"
                  startIcon={<Star />}
                  onClick={handleWriteReview}
                >
                  Review Guest
                </Button>
              )}
              {(booking.status === 'pending' || booking.status === 'confirmed') && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCancel}
                >
                  Cancel Booking
                </Button>
              )}
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <GuestDetailsPanel
            booking={booking}
            listingTitle={listing?.title}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <BookingConfirmationStatus bookingId={booking.bookingId} />
            <BookingCommunicationPanel
              booking={booking}
              listingTitle={listing?.title}
            />
          </Box>
        </TabPanel>
      </Box>



      {/* Edit Booking Form */}
      <BookingForm
        open={editFormOpen}
        onClose={() => setEditFormOpen(false)}
        onSubmit={handleUpdateBooking}
        booking={booking}
        listings={listing ? [listing] : []}
      />

      {/* Review Prompt Modal */}
      <ReviewPromptModal
        open={reviewModalOpen}
        onClose={handleReviewModalClose}
        booking={booking}
        onReviewSubmitted={handleReviewSubmitted}
      />
    </SidebarLayout>
  );
}